import { motion } from "framer-motion";
import { Code2, Database, Cpu, Server, Boxes, ShieldCheck } from "lucide-react";
import { Eyebrow, fadeUp } from "@/components/landing/shared";
import { useSiteContent } from "@/context/ContentContext";

const STAT_ICONS = [
  { icon: Server, label: "Backend" },
  { icon: Code2, label: "Frontend" },
  { icon: Database, label: "Database" },
  { icon: Boxes, label: "API" },
  { icon: Cpu, label: "AI" },
  { icon: ShieldCheck, label: "Sicurezza" },
];

export default function AboutSection() {
  const { about, images } = useSiteContent();
  const builds = about.builds || [];
  return (
    <section
      id="gerardo"
      className="relative scroll-mt-20 border-t border-black/[0.08] py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Eyebrow>{about.eyebrow}</Eyebrow>
            <motion.h2
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-4xl"
            >
              {about.title}
            </motion.h2>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              className="mt-6 space-y-4 text-base leading-relaxed text-secondary"
            >
              <p>{about.p1}</p>
              <p>{about.p2}</p>
            </motion.div>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {builds.map((b, i) => (
                <span
                  key={`${b}-${i}`}
                  className="inline-flex items-center rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-secondary"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="lg:col-span-5"
          >
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-gradient-to-b from-black/[0.04] to-transparent">
              {images?.about ? (
                <div className="relative h-40 w-full overflow-hidden border-b border-black/[0.08]">
                  <img
                    src={images.about}
                    alt="Ambiente di lavoro: sviluppo software su misura"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(255,255,255,0.35), transparent 60%)",
                    }}
                  />
                </div>
              ) : null}
              <div className="p-7">
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-emerald-400/40 bg-emerald-600/10 font-display text-lg font-bold text-emerald-700">
                    GF
                  </span>
                  <div>
                    <p className="font-display text-base font-semibold text-[color:var(--text-primary)]">
                      {about.name}
                    </p>
                    <p className="text-sm text-tertiary">{about.role}</p>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {STAT_ICONS.map((x) => (
                    <div
                      key={x.label}
                      className="flex flex-col items-center gap-2 rounded-lg border border-black/[0.08] bg-black/[0.02] py-3"
                    >
                      <x.icon className="h-4 w-4 text-emerald-600" />
                      <span className="text-[11px] text-tertiary">{x.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 grid grid-cols-2 gap-4 border-t border-black/[0.08] pt-6">
                  <div>
                    <p className="font-display text-2xl font-semibold text-[color:var(--text-primary)] tabnum">
                      {about.years}
                    </p>
                    <p className="text-xs text-tertiary">{about.years_label}</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-semibold text-[color:var(--text-primary)] tabnum">
                      {about.saas}
                    </p>
                    <p className="text-xs text-tertiary">{about.saas_label}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
